import type { StudentDomain } from '@mathflat/domain/@entities/Student/domain'
import { AxiosError } from 'axios'
import { useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { loadInitialData } from '~/@app/init/loadInitialData'
import { parentApi } from '~/@common/api/parentApi'
import { routeName } from '~/@common/constants'
import authService from '~/@common/services/auth.service'
import { localStorageService } from '~/@common/services/storage.service'
import { toastService } from '~/@common/services/toast.service'
import { FloatingLoader } from '~/@common/ui/(Loader)/FloatingLoader'
import { getEntrancePage } from '~/@common/utils/getEntrancePage'
import maxios from '~/@common/utils/maxios'
import { getUserType } from '~/@common/utils/userUtils'

import { guestLoginService } from './guestLoginService'

// 게스트 로그인 URL 쿼리 파라미터 스펙
type GuestLoginQueryParams = {
  id: string // AES-GCM 암호화된 로그인 ID (hex)
  pw: string // AES-GCM 암호화된 비밀번호 (hex)
  key: string // 암호화 키 (hex)
  ivId: string // id 복호화용 IV (hex)
  ivPw: string // pw 복호화용 IV (hex)
  redirectURI?: string // 로그인 성공 후 이동할 대상 경로 (선택)
}

// 로그인 자격 증명
type LoginCredential = {
  id: string
  password: string
}

/**
 * 외부 시연용 게스트 로그인 페이지.
 *
 * URL 쿼리로 암호화된 ID/PW를 받아 복호화 → 일반 로그인 API(loginByJwt) 호출.
 * 성공 시 역할별 진입 페이지로 이동하며, redirectURI가 있으면 쿼리로 전달한다.
 *
 * ⚠️ 외부에 공유된 magic URL(`/83f7e1b6-...`)을 통해 진입하므로 경로 변경 금지.
 */
const GuestLoginPage = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // GuestLoginQueryParams 스펙에 맞춰 쿼리 파라미터를 타입이 있는 형태로 추출
  const queryParams: Partial<GuestLoginQueryParams> = {
    id: searchParams.get('id') ?? undefined,
    pw: searchParams.get('pw') ?? undefined,
    key: searchParams.get('key') ?? undefined,
    ivId: searchParams.get('ivId') ?? undefined,
    ivPw: searchParams.get('ivPw') ?? undefined,
    redirectURI: searchParams.get('redirectURI') ?? undefined,
  }
  const { id: encryptedId, pw: encryptedPw, key, ivId, ivPw, redirectURI } = queryParams

  useEffect(() => {
    // 에러 발생 시 토스트 + 로그인 페이지로 복귀
    const handleError = (errorMessage = '비정상적인 접근입니다.') => {
      toastService.error(errorMessage)
      navigate(routeName.login, { replace: true })
    }

    // 실제 로그인 수행
    const login = async ({ id, password }: LoginCredential) => {
      try {
        const userType = getUserType(id[0])
        maxios.defaults.headers.common['x-platform'] = userType

        // 게스트 로그인은 시연용이므로 "로그인 유지" 체크박스 ON과 동일하게 처리
        const user = await authService.loginByJwt(
          {
            id,
            password,
            userType,
            serviceType: 'MATHFLAT',
          },
          true,
          id,
        )

        // 선생님 계정은 학생앱에 로그인 불가 — 이미 저장된 JWT 세션 정리 후 리다이렉트
        if (user.userType === 'TEACHER') {
          toastService.error('학생 계정으로 로그인해주세요.')
          await authService.logout()
          navigate(routeName.login, { replace: true })
          return
        }

        // redirectURI가 있으면 쿼리스트링으로 목적지에 전달 (민감 쿼리는 의도적으로 버림)
        const queryString = redirectURI ? `?redirectURI=${encodeURIComponent(redirectURI)}` : ''

        if (user.userType === 'PARENT') {
          try {
            const { studentIds } = await parentApi.getParentRelations(user.userId)
            const studentId = studentIds[0] as StudentDomain.Id
            localStorageService.update({ user: { studentId } })
            await loadInitialData(studentId)
          } catch {
            // 학부모-학생 관계 조회 실패 시 로그아웃 처리
            await authService.logout()
            return
          }
          navigate(getEntrancePage('PARENT') + queryString, { replace: true })
          return
        }

        // STUDENT: loginByJwt 내부에서 loadInitialData까지 이미 실행됨
        navigate(getEntrancePage('STUDENT') + queryString, { replace: true })
      } catch (error) {
        // 서버 에러 코드별 안내 토스트
        if (error instanceof AxiosError) {
          const code = error.response?.data?.code

          if (code === 'USER_NOT_FOUND') {
            toastService.error('아이디를 다시 확인해주세요.')
          } else if (code === 'USER_WRONG_PASSWORD') {
            toastService.error('비밀번호를 다시 확인해주세요.')
          } else if (code === 'USER_EXPIRED') {
            toastService.error('현재 유저는 사용중지되었습니다.')
          } else {
            toastService.error('로그인에 실패했습니다. 다시 시도해주세요.')
          }
        } else {
          toastService.error('로그인에 실패했습니다. 다시 시도해주세요.')
        }
        navigate(routeName.login, { replace: true })
      }
    }

    // 필수 쿼리 누락 검증
    if (!encryptedId || !encryptedPw || !key || !ivId || !ivPw) {
      handleError()
      return
    }

    // 복호화 → 로그인
    ;(async () => {
      try {
        const decryptedId = await guestLoginService.decryptAES(encryptedId, key, ivId)
        const decryptedPw = await guestLoginService.decryptAES(encryptedPw, key, ivPw)

        if (!decryptedId || !decryptedPw) {
          handleError()
          return
        }

        await login({ id: decryptedId, password: decryptedPw })
      } catch {
        handleError()
      }
    })()
  }, [encryptedId, encryptedPw, key, ivId, ivPw, redirectURI, navigate])

  return <FloatingLoader />
}

export default GuestLoginPage
